const kgToTnThreshold = 500000;

export const formatKg = (value) =>
    new Intl.NumberFormat("es-ES").format(value);

/**
 * Formatea una cantidad expresada en kilogramos.
 * Muestra toneladas (tn) cuando el valor es >= 500.000 kg, kilogramos en caso contrario.
 */
export const formatPeso = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return "—";

    if (num >= kgToTnThreshold) {
        return `${new Intl.NumberFormat("es-ES", {
            maximumFractionDigits: 2,
        }).format(num / 1000)} tn`;
    }
    return `${new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: 2,
    }).format(num)} kg`;
};

/* Los peligrosos se miden en kilos y los no peligrosos en toneladas. Misma regla
   que la aplicación antigua (Views/Obras/Details.cshtml) y que PesoConUnidad en
   ResiduosObraRepository. El backend siempre manda y espera Peso en kg.

   Los códigos van por zona (ResiduosTipo.CodZona), un par RP/RNP por zona: 1 y 2
   Europa, 3 y 4 México, 5 y 6 Países no GOA. Se enumeran los no peligrosos (RNP),
   no los peligrosos, para que un tipo desconocido caiga en kilos: es la unidad del
   dato, así que no convertir es lo único que no puede falsear un valor por un
   factor de 1000. */
const TIPOS_RESIDUO_EN_TN = [2, 4, 6];

export const unidadResiduo = (codTipoResiduo) =>
    TIPOS_RESIDUO_EN_TN.includes(Number(codTipoResiduo)) ? "tn" : "kg";

/**
 * Convierte un peso en kilogramos a la unidad que corresponde al tipo de residuo.
 * Devuelve el número, sin formatear, para poder seguir haciendo cuentas con él
 * (barras de valorización, totales...).
 */
export const pesoEnUnidadResiduo = (pesoKg, codTipoResiduo) => {
    const num = Number(pesoKg);
    if (!Number.isFinite(num)) return 0;

    return unidadResiduo(codTipoResiduo) === "kg" ? num : num / 1000;
};

/**
 * Inversa de `pesoEnUnidadResiduo`: pasa a kilogramos un peso escrito en la unidad
 * del tipo de residuo. Es lo que hay que guardar, porque el backend siempre
 * espera kg (y así lo hacía la aplicación antigua al enviar el formulario).
 */
export const pesoAKilogramos = (peso, codTipoResiduo) => {
    const num = Number(peso);
    if (!Number.isFinite(num)) return 0;

    if (unidadResiduo(codTipoResiduo) === "kg") return num;

    /* 1,1 * 1000 da 1100.0000000000002 en coma flotante, y eso es lo que se
       guardaría. Se redondea al gramo, que es de sobra para un peso. */
    return Math.round(num * 1000 * 1000) / 1000;
};

/**
 * Peso de una línea de residuo tal y como lo mostraba la aplicación antigua:
 * kg para los peligrosos, tn para el resto.
 */
export const formatPesoResiduo = (pesoKg, codTipoResiduo) => {
    const num = Number(pesoKg);
    if (!Number.isFinite(num)) return "—";

    const unidad = unidadResiduo(codTipoResiduo);

    return `${new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: unidad === "kg" ? 2 : 3,
    }).format(pesoEnUnidadResiduo(num, codTipoResiduo))} ${unidad}`;
};

/**
 * Formatea un peso según el tipo de residuo:
 * peligroso (esPeligroso = true) -> kilogramos (kg),
 * no peligroso -> toneladas (tn).
 */
export const formatPesoPorTipo = (value, esPeligroso) => {
    const num = Number(value);
    if (Number.isNaN(num)) return "—";

    if (esPeligroso) {
        return `${new Intl.NumberFormat("es-ES", {
            maximumFractionDigits: 2,
        }).format(num)} kg`;
    }
    return `${new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: 2,
    }).format(num / 1000)} tn`;
};
